<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Kiểm tra hoa hồng — sản phẩm trùng nhiều nhóm'">
          <template v-slot:preview>
            <div class="text-muted mb-5">
              Sản phẩm thuộc nhiều nhóm: nhóm tạo mới nhất được áp dụng.
            </div>

            <!-- Thẻ tổng quan -->
            <b-row class="mb-5">
              <b-col md="3">
                <div class="overlap-card">
                  <div class="overlap-card__label">Sản phẩm bị trùng nhóm</div>
                  <div class="overlap-card__value text-danger">
                    {{ summary.totalDuplicatedProducts || 0 }}
                  </div>
                </div>
              </b-col>
              <b-col md="3">
                <div class="overlap-card">
                  <div class="overlap-card__label">Tổng nhóm hoa hồng</div>
                  <div class="overlap-card__value">
                    {{ summary.totalCommissionCategories || 0 }}
                  </div>
                </div>
              </b-col>
              <b-col md="6">
                <div class="overlap-card">
                  <div class="overlap-card__label">Quy tắc chọn nhóm</div>
                  <div class="overlap-card__rule">{{ summary.rule || '' }}</div>
                </div>
              </b-col>
            </b-row>

            <!-- Nhóm dính trùng nhiều nhất -->
            <div v-if="summary.topCategories && summary.topCategories.length" class="mb-5">
              <p class="font-weight-bold mb-2">Nhóm dính trùng nhiều nhất:</p>
              <span
                v-for="cat in summary.topCategories"
                :key="cat.id"
                class="badge badge-secondary mr-2 mb-2 overlap-badge"
                @click="filterByCategory(cat.id)"
                :title="'Bấm để lọc theo nhóm ' + cat.name"
              >
                {{ cat.name }} — {{ cat.productCount }} SP
              </span>
            </div>

            <!-- Bộ lọc -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col md="4" class="pl-0">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo mã / tên sản phẩm"
                    v-model="filter.keyword"
                    autocomplete="off"
                    v-on:input="debounceSearch"
                  ></b-input>
                </b-col>
                <b-col md="4">
                  <b-form-select
                    size="sm"
                    v-model="filter.commissionCategoryId"
                    :options="categoryOptions"
                    value-field="id"
                    text-field="name"
                    @change="onFilter"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Tất cả nhóm hoa hồng --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="1">
                  <b-button
                    style="width: 70px"
                    variant="primary"
                    size="sm"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <!-- Bảng dữ liệu -->
            <b-table
              :items="items"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              empty-text="Không có sản phẩm nào nằm trong nhiều nhóm hoa hồng"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="5"></vcl-table>
              </template>

              <template v-slot:cell(count)="row">
                <span>{{ (page - 1) * PAGE_LIMIT + row.index + 1 }}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="font-weight-bold">{{ row.item.productCode }}</div>
                <div class="text-muted small">{{ row.item.productName }}</div>
              </template>

              <template v-slot:cell(groupCount)="row">
                <span
                  class="badge"
                  :class="row.item.groupCount >= 3 ? 'badge-danger' : 'badge-warning'"
                >
                  {{ row.item.groupCount }} nhóm
                </span>
              </template>

              <template v-slot:cell(winning)="row">
                <div class="font-weight-bold text-success">
                  {{ row.item.winningCategoryName }}
                </div>
                <div class="text-muted small">ID {{ row.item.winningCategoryId }}</div>
              </template>

              <template v-slot:cell(groups)="row">
                <div
                  v-for="(group, idx) in row.item.groups"
                  :key="group.id"
                  class="overlap-group"
                  :class="{ 'overlap-group--win': idx === 0 }"
                >
                  <span class="overlap-group__mark">{{ idx === 0 ? '▶' : '·' }}</span>
                  <span class="overlap-group__name">{{ group.name }}</span>
                  <span class="badge badge-light ml-1">{{ group.viaName }}</span>
                  <span class="text-muted small ml-1">
                    tạo {{ formatShortDate(group.createdAt) }}
                  </span>
                </div>
              </template>
            </b-table>

            <!-- Phân trang -->
            <b-row>
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số sản phẩm bị trùng nhóm: {{ totalItems }}
                </p>
              </b-col>
              <b-col>
                <b-pagination-nav
                  class="custom-pagination"
                  v-show="totalPages >= 2"
                  :link-gen="linkGen"
                  :number-of-pages="totalPages"
                  use-router
                  @change="fetchData"
                  align="right"
                  first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                  prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                  next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                  last-class="page-item-last btn btn-icon btn-sm my-1"
                  page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
                >
                  <template v-slot:first-text>
                    <span>
                      <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                    </span>
                  </template>
                  <template v-slot:prev-text>
                    <i class="ki ki-bold-arrow-back icon-xs"></i>
                  </template>
                  <template v-slot:next-text>
                    <i class="ki ki-bold-arrow-next icon-xs"></i>
                  </template>
                  <template v-slot:last-text>
                    <span class="text-info">
                      <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                    </span>
                  </template>
                </b-pagination-nav>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import decounce from 'debounce';
import { VclTable } from 'vue-content-loading';
import { TIME_TRIGGER } from '@/utils/constants';
import { formatDate, makeToastFaile } from '@/utils/common';

const PAGE_LIMIT = 25;

export default {
  components: {
    KTCodePreview,
    VclTable,
  },
  data() {
    return {
      PAGE_LIMIT,
      onLoading: false,
      items: [],
      categoryOptions: [],
      summary: {},
      totalPages: 0,
      totalItems: 0,
      page: 1,
      filter: {
        keyword: '',
        commissionCategoryId: null,
      },
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'product',
          label: 'Sản phẩm',
          thStyle: { fontWeight: 600, color: '#181c32', width: '25%' },
        },
        {
          key: 'groupCount',
          label: 'Số nhóm',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
          tdClass: 'text-center',
        },
        {
          key: 'winning',
          label: 'Nhóm áp dụng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
        },
        {
          key: 'groups',
          label: 'Các nhóm chứa sản phẩm',
          thStyle: { fontWeight: 600, color: '#181c32', width: '40%' },
        },
      ],
    };
  },
  created() {
    this.page = Number(this.$route.query.page) || 1;
    this.fetchCategories();
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Kiểm tra hoa hồng' },
    ]);
  },
  methods: {
    formatShortDate(value) {
      return value ? formatDate(value, 'DD/MM/YYYY') : '';
    },
    debounceSearch: decounce(function () {
      this.onFilter();
    }, TIME_TRIGGER),
    onFilter() {
      this.page = 1;
      this.$router.push({ query: {} }).catch(() => {});
      this.fetchData();
    },
    filterByCategory(categoryId) {
      this.filter.commissionCategoryId = categoryId;
      this.onFilter();
    },
    /** Nạp danh sách nhóm cho dropdown lọc */
    fetchCategories() {
      ApiService.setHeader();
      ApiService.get('commission-categories?limit=200')
        .then((response) => {
          this.categoryOptions = (response.data.data.data || []).map((item) => ({
            id: item.id,
            name: item.name,
          }));
        })
        .catch(() => {
          this.categoryOptions = [];
        });
    },
    fetchData(pageNum) {
      this.page = pageNum || Number(this.$route.query.page) || 1;
      this.onLoading = true;

      const params = new URLSearchParams();
      params.append('page', this.page);
      params.append('limit', PAGE_LIMIT);
      if (this.filter.keyword) {
        params.append('keyword', this.filter.keyword);
      }
      if (this.filter.commissionCategoryId) {
        params.append('commissionCategoryId', this.filter.commissionCategoryId);
      }

      ApiService.setHeader();
      ApiService.get(`commission-overlaps?${params.toString()}`)
        .then((response) => {
          const payload = response.data.data;
          this.items = payload.data || [];
          this.summary = payload.summary || {};
          this.totalItems = payload.totalRow || 0;
          this.totalPages = payload.totalPage || 1;
          this.onLoading = false;
        })
        .catch((err) => {
          this.onLoading = false;
          const message = err.response
            ? err.response.data.message
            : 'Lỗi khi tải dữ liệu kiểm tra hoa hồng';
          makeToastFaile(message);
        });
    },
  },
};
</script>

<style scoped>
.overlap-card {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
  height: 100%;
}
.overlap-card__label {
  color: #7e8299;
  font-size: 12px;
  margin-bottom: 4px;
}
.overlap-card__value {
  font-size: 22px;
  font-weight: 700;
  color: #181c32;
}
.overlap-card__rule {
  font-size: 12px;
  color: #3f4254;
  line-height: 1.5;
}
.overlap-badge {
  cursor: pointer;
  font-size: 11px;
  padding: 6px 10px;
}
.overlap-group {
  font-size: 12px;
  padding: 2px 0;
  color: #7e8299;
}
.overlap-group--win {
  color: #181c32;
  font-weight: 600;
}
.overlap-group__mark {
  display: inline-block;
  width: 14px;
}
.overlap-group__name {
  display: inline-block;
  min-width: 170px;
}
</style>
